import { ExternalLink } from 'lucide-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PageHeader } from '../../components/common/PageHeader';
import { appRoutes } from '../../app-routes';
import { VoiceUsageTab } from './VoiceUsageTab';
import { CallAnalysisTagsTab } from './call-analysis-tags/CallAnalysisTagsTab';

type TabName = 'usage' | 'call-tags';

const VALID_TABS: TabName[] = ['usage', 'call-tags'];

function getValidTab(tab: string | undefined): TabName {
  if (VALID_TABS.includes(tab as TabName)) return tab as TabName;
  return 'usage';
}

const TAB_COMPONENTS: Record<TabName, React.FC> = {
  usage: VoiceUsageTab,
  'call-tags': CallAnalysisTagsTab,
};

export const VoiceNavigator: React.FC = () => {
  const { tab = 'usage' } = useParams();
  const navigate = useNavigate();

  const activeTab = getValidTab(tab);

  useEffect(() => {
    if (!VALID_TABS.includes(tab as TabName)) {
      navigate(`${appRoutes.VOICE}/usage`, { replace: true });
    }
  }, [tab, navigate]);

  const handleTabChange = (newTab: string) => {
    const tabToNavigateTo = getValidTab(newTab);
    navigate(`${appRoutes.VOICE}/${tabToNavigateTo}`, { replace: true });
  };

  const TabComponent = TAB_COMPONENTS[activeTab];

  return (
    <section className="space-y-6">
      <PageHeader
        title="Assist"
        description="AI voice agent management and call analytics"
        right={
          <a
            href="https://docs.embedreach.com/ai-voice/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            API Docs
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        }
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-2">
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="call-tags">Call Tags</TabsTrigger>
        </TabsList>
        <TabComponent />
      </Tabs>
    </section>
  );
};
